import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// ── Breadcrumbs: buffer circular de últimas 20 acciones ──
const BREADCRUMB_LIMIT = 20;
const breadcrumbs: Array<{ ts: string; type: string; message: string }> = [];

export function addBreadcrumb(type: string, message: string): void {
  breadcrumbs.push({ ts: new Date().toISOString(), type, message });
  if (breadcrumbs.length > BREADCRUMB_LIMIT) breadcrumbs.shift();
}

// ── Sentry init (solo si hay DSN) ──
export function initSentry(): void {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (DSN) {
    Sentry.captureException(err, { extra: context });
  }
}

// ── Reporte al backend custom (DB + correo throttled) ──
export function reportClientError(err: unknown, context?: Record<string, unknown>): void {
  const message = err instanceof Error ? err.message : String(err);
  const stack = err instanceof Error ? err.stack : undefined;

  captureException(err, context);

  try {
    fetch("/api/client-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        breadcrumbs: breadcrumbs.slice(),
        ...context,
      }),
    }).catch(() => {});
  } catch {
    // Silently fail
  }
}

// ── Captura global de errores no capturados ──
export function installGlobalErrorHandlers(): void {
  window.addEventListener("error", (e) => {
    addBreadcrumb("error", e.message);
    reportClientError(e.error ?? new Error(e.message), {
      source: "window.onerror",
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const err = e.reason instanceof Error ? e.reason : new Error(String(e.reason));
    addBreadcrumb("promise", err.message);
    reportClientError(err, { source: "unhandledrejection" });
  });

  // Breadcrumb automático: clicks
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const label = target?.tagName + (target?.id ? `#${target.id}` : "") + (target?.className ? `.${String(target.className).split(" ")[0]}` : "");
    addBreadcrumb("click", label);
  }, { passive: true });

  // Breadcrumb automático: navegación (hash/route changes)
  let lastUrl = window.location.href;
  const observer = new MutationObserver(() => {
    if (window.location.href !== lastUrl) {
      addBreadcrumb("navigate", window.location.pathname);
      lastUrl = window.location.href;
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
